import { NavLink } from 'react-router';
import {
  Activity,
  Building2,
  CreditCard,
  FileText,
  LayoutDashboard,
  Layers,
  PhoneCall,
  Ticket,
  Users,
  UsersRound,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  adminOnly: boolean;
  /**
   * The destination is on the roadmap but has no route or page yet. Rendered
   * as a disabled row, never a link: a live-looking link to an unrouted path
   * falls through to the catch-all and silently drops the user back on
   * /dashboard with no explanation. Drop this flag when the page lands.
   */
  comingSoon?: boolean;
}

export const NAV_ITEMS: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, adminOnly: false },
  { to: '/groups', label: 'Groups', icon: UsersRound, adminOnly: false },
  { to: '/subscriptions', label: 'Subscriptions', icon: Layers, adminOnly: false },
  { to: '/revenue', label: 'Revenue', icon: CreditCard, adminOnly: false },
  { to: '/follow-up', label: 'Follow-up', icon: PhoneCall, adminOnly: false },
  { to: '/plans', label: 'Plans', icon: Building2, adminOnly: false },
  { to: '/promo-codes', label: 'Promo codes', icon: Ticket, adminOnly: false },
  { to: '/invoices', label: 'Invoices', icon: FileText, adminOnly: false },
  { to: '/activity', label: 'Activity', icon: Activity, adminOnly: true },
  { to: '/partners', label: 'Partners', icon: Users, adminOnly: true },
];

export function SidebarNav({ role, onNavigate }: { role?: 'admin' | 'support'; onNavigate?: () => void }) {
  const items = NAV_ITEMS.filter((item) => !item.adminOnly || role === 'admin');

  return (
    <nav aria-label="Main" className="space-y-1 px-3 py-4">
      {items.map((item) =>
        item.comingSoon ? (
          <span
            key={item.to}
            aria-disabled="true"
            className="flex cursor-not-allowed items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-primary-foreground/40"
          >
            <item.icon className="h-4 w-4" />
            {item.label}
            <span className="ml-auto rounded-full bg-primary-foreground/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
              Soon
            </span>
          </span>
        ) : (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-accent text-accent-foreground shadow-accent'
                  : 'text-primary-foreground/75 hover:bg-primary-foreground/10 hover:text-primary-foreground',
              )
            }
          >
            <item.icon className="h-4 w-4" />
            {item.label}
          </NavLink>
        ),
      )}
    </nav>
  );
}
